import { createBrowserRouter } from 'react-router-dom';
import { Suspense } from 'react';
import MainLayout from '../components/layout/MainLayout';
import MercaderistaLayout from '../components/layout/MercaderistaLayout';
import LoadingScreen from '../components/ui/LoadingScreen';
import ProtectedRoute from '../components/layout/ProtectedRoute';
import RoleBasedRedirect from '../components/layout/RoleBasedRedirect';
import RouteErrorElement from '../components/layout/RouteErrorElement';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import type { UserRole } from '../types';

// Lazy loading de páginas con auto-recovery para chunks obsoletos post-deploy
const LoginPage = lazyWithRetry(() => import('../pages/auth/LoginPage'));
const HomePage = lazyWithRetry(() => import('../pages/home/HomePage'));
const ProduccionPage = lazyWithRetry(() => import('../pages/produccion/ProduccionPage'));
const PedidosPage = lazyWithRetry(() => import('../pages/pedidos/PedidosPage'));
const DespachosPage = lazyWithRetry(() => import('../pages/despachos/DespachosPage'));
const CadenasPage = lazyWithRetry(() => import('../pages/clientes/CadenasPage'));
const CalidadPage = lazyWithRetry(() => import('../pages/calidad/CalidadPage'));
const KPIProduccionPage = lazyWithRetry(() => import('../pages/kpi-produccion/KPIProduccionPage'));
const RegistrosPage = lazyWithRetry(() => import('../pages/registros/RegistrosPage'));
const InventariosPage = lazyWithRetry(() => import('../pages/inventarios/InventariosPage'));
const GastosPage = lazyWithRetry(() => import('../pages/gastos/GastosPage'));
const CarteraPage = lazyWithRetry(() => import('../pages/cartera/CarteraPage'));
const ComprasPage = lazyWithRetry(() => import('../pages/compras/ComprasPage').then(m => ({ default: m.ComprasPage })));
const ReportesPage = lazyWithRetry(() => import('../pages/reportes/ReportesPage'));
const MercadeoPage = lazyWithRetry(() => import('../pages/mercadeo/MercadeoPage'));
const MercaderistaMiDiaPage = lazyWithRetry(() => import('../pages/mercadeo/mercaderista/MiDiaPage'));
const CronogramaPage = lazyWithRetry(() => import('../pages/produccion/CronogramaPage'));
const DashboardComercialPage = lazyWithRetry(() => import('../pages/dashboard-comercial/DashboardComercialPage'));
const PedidoPublicoPage = lazyWithRetry(() => import('../pages/pedido-publico/PedidoPublicoPage'));
const OCRedirectPage = lazyWithRetry(() => import('../pages/compras/OCRedirectPage'));
const OCPreviewPage = lazyWithRetry(() => import('../pages/compras/OCPreviewPage'));
const CatalogoPage = lazyWithRetry(() => import('../pages/catalogo/CatalogoPage'));
const NotFoundPage = lazyWithRetry(() => import('../pages/NotFoundPage'));

// Wrapper para Suspense (inside layout — use content variant to avoid CLS)
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingScreen variant="content" />}>{children}</Suspense>
);

// Definición de permisos por rol
export const rolePermissions: Record<UserRole, string[]> = {
  gerente: ['*'], // Acceso total
  gerente_comercial: ['dashboard', 'dashboard-comercial', 'pedidos', 'despachos', 'clientes', 'mercadeo', 'inventarios', 'cartera', 'kpi-produccion', 'cronograma', 'batch', 'reportes', 'compras'],
  admin_fabrica: ['dashboard', 'batch', 'pedidos', 'gestion-calidad', 'registros', 'inventarios', 'despachos', 'kpi-produccion', 'cronograma', 'compras'],
  operario: ['dashboard', 'batch', 'registros', 'gestion-calidad'],
  mercaderista: ['mercadeo'],
  contadora: ['dashboard', 'gastos', 'cartera', 'compras'],
  auxiliar: ['dashboard', 'pedidos', 'registros', 'gestion-calidad', 'kpi-produccion', 'cronograma'],
  visita_invima: ['pedidos', 'despachos', 'batch', 'kpi-produccion', 'cronograma', 'registros', 'gestion-calidad'],
  socios: ['dashboard', 'reportes', 'kpi-produccion'],
  invitado: [
    'dashboard-comercial',
    'pedidos',
    'despachos',
    'mercadeo',
    'batch',
    'kpi-produccion',
    'cronograma',
    'gestion-calidad',
    'registros',
  ],
};

// Verificar si un rol tiene acceso a una ruta
export const hasAccess = (role: UserRole | undefined, route: string): boolean => {
  if (!role) return false;
  const permissions = rolePermissions[role];
  if (!permissions) return false; // Rol no definido en el sistema
  return permissions.includes('*') || permissions.includes(route);
};

// Información de roles para mostrar en UI
export const roleLabels: Record<UserRole, string> = {
  gerente: 'Gerente',
  gerente_comercial: 'Gerente Comercial',
  admin_fabrica: 'Admin Fábrica',
  operario: 'Operario',
  mercaderista: 'Mercaderista',
  contadora: 'Contadora',
  auxiliar: 'Auxiliar',
  visita_invima: 'Visita INVIMA',
  socios: 'Socios',
  invitado: 'Invitado',
};

// Router principal
export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/pedir/:token',
    element: (
      <SuspenseWrapper>
        <PedidoPublicoPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/oc/:numero',
    element: (
      <SuspenseWrapper>
        <OCRedirectPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/oc-preview',
    element: (
      <SuspenseWrapper>
        <OCPreviewPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorElement />,
    children: [
      // Redirección basada en rol
      {
        index: true,
        element: <RoleBasedRedirect />,
      },
      // Layout para mercaderistas (app simplificada)
      {
        element: <MercaderistaLayout />,
        children: [
          {
            path: 'mi-dia',
            element: (
              <SuspenseWrapper>
                <MercaderistaMiDiaPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      // Layout principal para otros roles
      {
        element: <MainLayout />,
        children: [
          {
            path: 'dashboard',
            element: (
              <SuspenseWrapper>
                <HomePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'batch',
            element: (
              <SuspenseWrapper>
                <ProduccionPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'cronograma',
            element: (
              <SuspenseWrapper>
                <CronogramaPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'pedidos',
            element: (
              <SuspenseWrapper>
                <PedidosPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'despachos',
            element: (
              <SuspenseWrapper>
                <DespachosPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'clientes/cadenas',
            element: (
              <SuspenseWrapper>
                <CadenasPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'kpi-produccion',
            element: (
              <SuspenseWrapper>
                <KPIProduccionPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'gestion-calidad',
            element: (
              <SuspenseWrapper>
                <CalidadPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'registros',
            element: (
              <SuspenseWrapper>
                <RegistrosPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'inventarios',
            element: (
              <SuspenseWrapper>
                <InventariosPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'gastos',
            element: (
              <SuspenseWrapper>
                <GastosPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'cartera',
            element: (
              <SuspenseWrapper>
                <CarteraPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'compras',
            element: (
              <SuspenseWrapper>
                <ComprasPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'reportes',
            element: (
              <SuspenseWrapper>
                <ReportesPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'mercadeo',
            element: (
              <SuspenseWrapper>
                <MercadeoPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'dashboard-comercial',
            element: (
              <SuspenseWrapper>
                <DashboardComercialPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'dashboard-comercial/tiendas',
            element: (
              <SuspenseWrapper>
                <DashboardComercialPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'catalogo',
            element: (
              <SuspenseWrapper>
                <CatalogoPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorElement />,
  },
]);
